import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Box, Typography, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  LocalFireDepartment as HotIcon,
  TrendingUp as TrendingIcon,
  FiberNew as NewIcon,
} from "@mui/icons-material";
import axios from "axios";
import LeftSidebar from "../components/LeftSidebar";
import RightSidebar from "../components/RightSidebar";
import PostMain from "../components/PostMain";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileWidgetDrawer from "../components/MobileWidgetDrawer";
import MobileNavDrawer from "../components/MobileNavDrawer";
import PopupDialog from "../components/PopupDialog";
import { useTheme } from "../components/ThemeContext";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../context/PageContext";
import { throttle } from "lodash";
import { DeviceContext } from "../context/DeviceContext";

const SIDEBAR_LEFT_WIDTH = 264;
const SIDEBAR_RIGHT_WIDTH = 320;
const TOPBAR_HEIGHT = 64;

const MemoizedLeftSidebar = React.memo(LeftSidebar);
const MemoizedRightSidebar = React.memo(RightSidebar);

// ── Filter tab button ────────────────────────────────────────────────────────
function FilterTab({ label, icon: Icon, active, onClick, theme }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<Icon sx={{ fontSize: "1rem !important" }} />}
      sx={{
        borderRadius: "10px",
        px: 2,
        py: 0.8,
        fontWeight: 600,
        fontSize: "0.875rem",
        textTransform: "none",
        whiteSpace: "nowrap",
        transition: "all 0.15s ease",
        ...(active
          ? {
              background: "linear-gradient(90deg, #00c950, #009966)",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(0,201,80,0.25)",
              "&:hover": { background: "linear-gradient(90deg, #00b547, #008055)" },
            }
          : {
              backgroundColor: theme.palette.background.card,
              color: theme.palette.text.secondary,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": {
                backgroundColor: theme.palette.background.highlight,
                color: theme.palette.text.primary,
              },
            }),
      }}
    >
      {label}
    </Button>
  );
}

function MainContent() {
  const { theme } = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [posts, setPosts] = useState([]);
  const { currentPage, setCurrentPage, viewMode } = useContext(PageContext);
  const { isMobile } = useContext(DeviceContext);
  const navigate = useNavigate();

  // Mobile left sidebar drawer
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);

  const normalizePostForFeed = useCallback((post) => {
    const imageUrls = Array.isArray(post.imageUrls) ? post.imageUrls : [];
    const normalizedImages = Array.isArray(post.images)
      ? post.images
      : imageUrls.map((url, index) => ({
          imageId: `${post.postId}-${index}`,
          url,
        }));
    return {
      ...post,
      images: normalizedImages,
      tags: Array.isArray(post.tags) ? post.tags : [],
      communityName: post.communityName || post.community?.name || "",
      userName: post.userName || post.user?.username || "",
      upvotes: post.upvotes ?? 0,
      downvotes: post.downvotes ?? 0,
      commentCount: post.commentCount ?? 0,
    };
  }, []);

  const fetchPostMain = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    try {
      const endpointByPage = {
        Hot: "/api/trending/posts/hot",
        Trending: "/api/trending/posts",
        New: "/api/trending/posts/new",
      };
      const endpoint = endpointByPage[currentPage] || "/api/trending/posts/hot";
      const pageSize = 20;
      const response = await axios.get(
        `${process.env.REACT_APP_SC_API_BASE_URL}${endpoint}`,
        { params: { page, size: pageSize } }
      );
      const rawData = Array.isArray(response.data) ? response.data : [];
      const data = rawData.map(normalizePostForFeed);
      setPosts((prevPosts) => {
        const newPosts = data.filter(
          (post) => !prevPosts.some((p) => p.postId === post.postId)
        );
        return [...prevPosts, ...newPosts];
      });
      setHasMore(data.length >= pageSize);
    } catch (error) {
      console.error("Error fetching trending feed posts:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [page, currentPage, hasMore, normalizePostForFeed]);

  useEffect(() => {
    fetchPostMain();
  }, [fetchPostMain]);

  useEffect(() => {
    setPage(0);
    setPosts([]);
    setHasMore(true);
  }, [currentPage]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = throttle(() => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight * 0.75
      ) {
        if (hasMore && !loadingRef.current) {
          setPage((prev) => prev + 1);
        }
      }
    }, 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore]);

  const updatePosts = (postId, upvotes, downvotes) => {
    setPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.postId === postId ? { ...post, upvotes, downvotes } : post
      )
    );
  };

  // ── Filter tabs ─────────────────────────────────────────────────────────────
  const filterTabs = [
    { label: "Hot", key: "Hot", icon: HotIcon },
    { label: "Trending", key: "Trending", icon: TrendingIcon },
    { label: "New", key: "New", icon: NewIcon },
  ];

  const FilterTabsRow = () => (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        mb: 3,
        overflowX: "auto",
        pb: 0.5,
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {filterTabs.map((tab) => (
        <FilterTab
          key={tab.key}
          label={tab.label}
          icon={tab.icon}
          active={currentPage === tab.key}
          onClick={() => setCurrentPage(tab.key)}
          theme={theme}
        />
      ))}
    </Box>
  );

  // ── Posts renderers ──────────────────────────────────────────────────────────
  // Tile: CSS columns for true masonry — cards take natural height, no empty row gaps
  const tileGrid = (
    <Box
      sx={{
        columns: { xs: 1, sm: 2, md: 3 },
        columnGap: "16px",
      }}
    >
      {posts.map((post) => (
        <Box key={post.postId} sx={{ breakInside: "avoid", mb: 2 }}>
          <PostMain
            post={post}
            updatePosts={updatePosts}
            viewMode="tile"
          />
        </Box>
      ))}
    </Box>
  );

  const listFeed = (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {posts.map((post) => (
        <PostMain key={post.postId} post={post} updatePosts={updatePosts} viewMode="list" />
      ))}
    </Box>
  );

  const feedContent = viewMode === "tile" ? tileGrid : listFeed;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <PopupDialog open={openDialog} handleClose={() => setOpenDialog(false)} />

      {/* Fixed top bar — passes hamburger toggle callback for mobile */}
      <ResponsiveTopBar
        isMobile={isMobile}
        onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)}
      />

      {isMobile ? (
        // ══════════════════════════════════════════════════════════════════════
        // MOBILE LAYOUT  (figma pattern)
        // - Left sidebar as a slide-in Drawer from the left (hamburger triggers it)
        // - Feed is full-width
        // - FAB bottom-right opens widget bottom sheet
        // ══════════════════════════════════════════════════════════════════════
        <Box
          sx={{
            mt: `${TOPBAR_HEIGHT}px`,
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
          }}
        >
          {/* Mobile nav drawer — Figma hamburger design */}
          <MobileNavDrawer
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
          />

          {/* Main feed */}
          <Box sx={{ px: viewMode === "tile" ? 1 : 2, py: 2.5 }}>
            <FilterTabsRow />
            {feedContent}
          </Box>

          {/* FAB + Widget drawer (replaces old right-sidebar tab) */}
          <MobileWidgetDrawer />
        </Box>
      ) : (
        // ══════════════════════════════════════════════════════════════════════
        // DESKTOP LAYOUT  (fixed sidebars + scrollable center)
        // ══════════════════════════════════════════════════════════════════════
        <Box
          sx={{
            display: "flex",
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
            pt: `${TOPBAR_HEIGHT}px`,
          }}
        >
          {/* Left Sidebar — fixed, hidden below lg (1200px) */}
          <Box
            sx={{
              display: { xs: "none", md: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              left: 0,
              bottom: 0,
              width: SIDEBAR_LEFT_WIDTH,
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
              },
              zIndex: 100,
            }}
          >
            <MemoizedLeftSidebar />
          </Box>

          {/* Center content — scrollable, margins only when sidebars are visible */}
          <Box
            sx={{
              ml: { xs: 0, lg: `${SIDEBAR_LEFT_WIDTH}px` },
              mr: { xs: 0, lg: `${SIDEBAR_RIGHT_WIDTH}px` },
              flex: 1,
              minWidth: 0,
              py: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {/* Inner content column — list view capped at 800px, tile view fills width */}
            <Box
              sx={{
                width: "100%",
                maxWidth: viewMode === "tile" ? "none" : 800,
                px: viewMode === "tile" ? { md: 2, lg: 3 } : { md: 3, lg: 4 },
              }}
            >
              <FilterTabsRow />
              {feedContent}
            </Box>
          </Box>

          {/* Right Sidebar — fixed, hidden below lg (1200px) */}
          <Box
            sx={{
              display: { xs: "none", md: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              right: 0,
              bottom: 0,
              width: SIDEBAR_RIGHT_WIDTH,
              backgroundColor: theme.palette.background.paper,
              borderLeft: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
              },
              zIndex: 100,
            }}
          >
            <MemoizedRightSidebar />
          </Box>
        </Box>
      )}
    </>
  );
}

export default MainContent;
